import { useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, RotateCw } from 'lucide-react';

const SPEEDS = [1, 1.25, 1.5, 2, 0.75] as const;
import ReactMarkdown from 'react-markdown';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGeneratePodcast } from '../../api/queries';
import { useAppParams } from '../../api/params';
import { getFileUrl } from '../../api/client';
import styles from './PodcastsView.module.css';

export function PodcastsView() {
  const mut = useGeneratePodcast();
  const params = useAppParams();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState<number>(1);

  const cycleRate = () => {
    const idx = SPEEDS.indexOf(rate as (typeof SPEEDS)[number]);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending) return <LoadingState label="Grabando el pódcast…" hint="Tardamos un par de minutos en sintetizar la voz" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const file: string = data.content?.file ?? '';
  const script: string = toScriptMarkdown(data.content?.script);
  const src = getFileUrl(file, params);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };
  const seek = (delta: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(el.duration || 0, el.currentTime + delta));
  };
  const onClickBar = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    el.currentTime = pct * duration;
  };

  const fmt = (s: number) => {
    if (!Number.isFinite(s)) return '--:--';
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.view}>
      <div className={styles.cover} aria-hidden>
        <Bars playing={playing} />
      </div>
      <h2 className={styles.title}>{data.title ?? 'Pódcast del módulo'}</h2>
      {data.description && <p className={styles.subtitle}>{data.description}</p>}

      <div className={styles.player}>
        <div className={styles.bar} onClick={onClickBar} role="progressbar" aria-valuemax={duration} aria-valuenow={progress}>
          <div className={styles.barFill} style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
        </div>
        <div className={styles.times}>
          <span>{fmt(progress)}</span>
          <span>{fmt(duration)}</span>
        </div>
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.speed}
            onClick={cycleRate}
            aria-label={`Velocidad de reproducción: ${rate}x`}
            title="Velocidad"
          >
            {rate}x
          </button>
          <button
            type="button"
            className={styles.skip}
            onClick={() => seek(-15)}
            aria-label="Retroceder 15 segundos"
            title="-15s"
          >
            <RotateCcw size={24} strokeWidth={1.5} />
            <span className={styles.skipNum}>15</span>
          </button>
          <button type="button" className={styles.play} onClick={toggle} aria-label={playing ? 'Pausa' : 'Reproducir'}>
            {playing ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <button
            type="button"
            className={styles.skip}
            onClick={() => seek(15)}
            aria-label="Avanzar 15 segundos"
            title="+15s"
          >
            <RotateCw size={24} strokeWidth={1.5} />
            <span className={styles.skipNum}>15</span>
          </button>
          <span className={styles.controlsSpacer} aria-hidden />
        </div>
        <audio
          ref={audioRef}
          src={src}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setProgress((e.target as HTMLAudioElement).currentTime)}
          onLoadedMetadata={(e) => setDuration((e.target as HTMLAudioElement).duration)}
        />
      </div>

      {script && (
        <details className={styles.scriptBox}>
          <summary>Ver transcripción del pódcast</summary>
          <ReactMarkdown>{script}</ReactMarkdown>
        </details>
      )}
    </div>
  );
}

// Strip SSML markup (the TTS-side tags that travel inside `content`)
// so the transcript reads naturally. Collapses repeated whitespace too.
const stripSsml = (s: string) =>
  s
    .replace(/<break[^>]*\/?>/gi, ' ')
    .replace(/<\/?[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Backend shapes seen in the wild:
//   - string
//   - { conversation: [{ speaker, content }, ...] }
//   - [{ speaker, text }, ...]   (legacy)
//   - { speaker, text }          (single turn)
//
// `speaker` can be "male"/"female", "voice_1"/"voice_2", etc. — we just want
// stable "Speaker 1" / "Speaker 2" labels in encounter order.
function toScriptMarkdown(raw: unknown): string {
  if (!raw) return '';
  if (typeof raw === 'string') return stripSsml(raw);
  if (typeof raw === 'object' && 'conversation' in (raw as any))
    return toScriptMarkdown((raw as any).conversation);

  const labels = new Map<string, string>();
  const labelFor = (key: string | undefined) => {
    if (!key) return '';
    if (!labels.has(key)) labels.set(key, `Podcaster ${labels.size + 1}`);
    return labels.get(key)!;
  };

  const turn = (t: any): string => {
    if (typeof t === 'string') return stripSsml(t);
    const rawSpeaker = t?.speaker ?? t?.role ?? t?.name;
    const text = stripSsml(String(t?.text ?? t?.content ?? t?.line ?? ''));
    const label = labelFor(rawSpeaker != null ? String(rawSpeaker) : undefined);
    return label ? `**${label}:** ${text}` : text;
  };
  if (Array.isArray(raw)) return raw.map(turn).join('\n\n');
  if (typeof raw === 'object') return turn(raw);
  return String(raw);
}

function Bars({ playing }: { playing: boolean }) {
  return (
    <div className={styles.bars} data-playing={playing}>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <span key={i} style={{ animationDelay: `${i * 80}ms` }} />
      ))}
    </div>
  );
}
